import { Link } from "react-router-dom";
import {
  Lock,
  Shield,
  Zap,
} from "lucide-react";
import pdfWorkspaceLogo from "@/assets/logo.png";
import { TOOL_SEO_ROUTES } from "@/config/toolSeoRegistry";

const quickTools = TOOL_SEO_ROUTES.filter((route) =>
  [
    "/tools/merge",
    "/tools/compress",
    "/tools/split",
    "/tools/convert/pdf-to-word",
    "/tools/word-to-pdf",
    "/batch-tools/compress",
  ].includes(route.path),
).map((route) => ({ name: route.name, href: route.path }));

const resourceLinks = [
  { name: "All Tools", href: "/tools" },
  { name: "Batch Tools", href: "/batch-tools" },
  { name: "P2P Tools", href: "/p2p-tools" },
  { name: "For Students", href: "/students" },
  { name: "For Small Business", href: "/business" },
  { name: "For Legal Work", href: "/legal" },
  { name: "Chat With PDF", href: "/tools/ai/chat" },
  { name: "PDF Security Audit", href: "/tools/pdf-security-audit" },
  { name: "FAQ", href: "/faq" },
  { name: "About & Privacy", href: "/about-privacy" },
  { name: "Blog", href: "/blog" },
];

const blogLinks = [
  { name: "Client-Side PDF Tools", href: "/blog/client-side-pdf-tools" },
  { name: "No-Server PDF Tools", href: "/blog/no-server-pdf-tools" },
  { name: "Private PDF Processing", href: "/blog/private-pdf-processing" },
  { name: "Secure PDF Tools", href: "/blog/secure-pdf-tools" },
];

const guideLinks = [
  { name: "Compress PDF for Email", href: "/how-to/compress-pdf-for-email" },
  { name: "Merge PDF on iPhone", href: "/how-to/merge-pdf-on-iphone" },
  { name: "OCR PDF in Hindi", href: "/how-to/ocr-pdf-in-hindi" },
  { name: "Split PDF for Lawyers", href: "/how-to/split-pdf-for-lawyers" },
  { name: "Sign PDF for Business", href: "/how-to/sign-pdf-for-business" },
  { name: "Edit PDF on Chromebook", href: "/how-to/edit-pdf-on-chromebook" },
];

export const Footer = () => {
  const year = new Date().getFullYear();

  return (
    <footer className="border-t border-border bg-background">
      {/* Top Section - CTA Banner */}
      {/* <div className="border-b border-border bg-secondary/30">
        <div className="mx-auto max-w-6xl px-4 py-10 md:py-12">
          <div className="flex flex-col items-center gap-6 text-center md:flex-row md:justify-between md:text-left">
            <div className="space-y-2">
              <h3 className="font-serifAccent text-2xl tracking-tight text-foreground md:text-3xl">
                Ready to work with your PDFs?
              </h3>
              <p className="text-sm text-text-secondary max-w-md">
                Start using PDFWorkSpace now. No signup, no downloads, no uploads –
                just open and use.
              </p>
            </div>
            <Link
              to="/tools"
              className="group inline-flex items-center gap-2 rounded-full bg-foreground px-8 py-3.5 text-sm font-medium text-background transition-all hover:bg-foreground/90 hover:gap-3"
            >
              Open tools
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </div> */}

      {/* Main Footer Content */}
      <div className="mx-auto max-w-6xl px-4 py-12 md:py-16">
        <div className="grid gap-8 md:grid-cols-6 md:gap-8">
          {/* Brand Section */}
          <div className="space-y-5 md:col-span-2">
            <Link to="/" className="inline-flex items-center gap-3">
              <img
                src={pdfWorkspaceLogo}
                alt="PDFWorkSpace logo"
                className="h-12 w-12"
              />
              <span className="font-serifAccent text-2xl tracking-tight text-foreground">
                PDFWorkSpace
              </span>
            </Link>
            <p className="text-sm text-text-secondary leading-relaxed max-w-xs">
              A local-first PDF toolkit designed for people who care about
              document privacy.
            </p>
            <div className="flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-75"></span>
                <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500"></span>
              </span>
              <span className="text-sm text-text-secondary">
                Your files never leave your device
              </span>
            </div>
          </div>

          {/* Quick Tools */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold uppercase tracking-wider text-foreground">
              Quick Tools
            </h4>
            <nav className="flex flex-col gap-2">
              {quickTools.map((tool) => (
                <Link
                  key={tool.name}
                  to={tool.href}
                  className="text-sm text-text-secondary transition-colors hover:text-foreground"
                >
                  {tool.name}
                </Link>
              ))}
            </nav>
          </div>

          {/* Blog */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold uppercase tracking-wider text-foreground">
              Blog
            </h4>
            <nav className="flex flex-col gap-2">
              <Link
                to="/blog"
                className="text-sm text-text-secondary transition-colors hover:text-foreground"
              >
                All Blog Posts
              </Link>
              {blogLinks.map((blog) => (
                <Link
                  key={blog.href}
                  to={blog.href}
                  className="text-sm text-text-secondary transition-colors hover:text-foreground"
                >
                  {blog.name}
                </Link>
              ))}
            </nav>
          </div>

          {/* Guides */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold uppercase tracking-wider text-foreground">
              Guides
            </h4>
            <nav className="flex flex-col gap-2">
              {guideLinks.map((guide) => (
                <Link
                  key={guide.href}
                  to={guide.href}
                  className="text-sm text-text-secondary transition-colors hover:text-foreground"
                >
                  {guide.name}
                </Link>
              ))}
            </nav>
          </div>

          {/* Resources */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold uppercase tracking-wider text-foreground">
              Resources
            </h4>
            <nav className="flex flex-col gap-2">
              {resourceLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-sm text-text-secondary transition-colors hover:text-foreground"
                >
                  {link.name}
                </Link>
              ))}
            </nav>
          </div>

          {/* Legal */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold uppercase tracking-wider text-foreground">
              Legal
            </h4>
            <nav className="flex flex-col gap-2">
              <Link
                to="/terms"
                className="text-sm text-text-secondary transition-colors hover:text-foreground"
              >
                Terms & Conditions
              </Link>
              <Link
                to="/privacy"
                className="text-sm text-text-secondary transition-colors hover:text-foreground"
              >
                Privacy Policy
              </Link>
              <a
                href="/llms.txt"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-text-secondary transition-colors hover:text-foreground"
              >
                LLMs.txt
              </a>
            </nav>
          </div>

          {/* Trust Badges */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold uppercase tracking-wider text-foreground">
              Why Us
            </h4>
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-text-secondary">
                <Shield className="h-4 w-4" />
                <span>No data collection</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-text-secondary">
                <Lock className="h-4 w-4" />
                <span>No accounts needed</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-text-secondary">
                <Zap className="h-4 w-4" />
                <span>Works offline</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-border">
        <div className="mx-auto max-w-6xl px-4 py-6">
          <div className="flex flex-col gap-4 text-sm text-text-secondary md:flex-row md:items-center md:justify-between">
            <div>
              <p>© {year} PDFWorkSpace. All rights reserved.</p>
              <p className="text-xs mt-1">
                Built with AI & late nights by{" "}
                <a
                  href="https://x.com/rahil1202"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-foreground hover:underline underline-offset-2"
                >
                  Rahil Vahora
                </a>{" "}
                — probably asking AI to build something right now.
              </p>
            </div>
            <div className="flex items-center gap-4">
              <a
                href="https://github.com/rahil1202"
                target="_blank"
                rel="noopener noreferrer"
                className="text-text-secondary hover:text-foreground transition-colors"
                aria-label="GitHub"
              >
                <svg
                  className="h-5 w-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    fillRule="evenodd"
                    d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
              <a
                href="https://linkedin.com/in/rahil-vahora"
                target="_blank"
                rel="noopener noreferrer"
                className="text-text-secondary hover:text-foreground transition-colors"
                aria-label="LinkedIn"
              >
                <svg
                  className="h-5 w-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </a>
              <a
                href="https://x.com/rahil1202"
                target="_blank"
                rel="noopener noreferrer"
                className="text-text-secondary hover:text-foreground transition-colors"
                aria-label="X (Twitter)"
              >
                <svg
                  className="h-5 w-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Large Brand Watermark */}
      <div className="overflow-hidden py-8 md:py-12">
        <h2 className="text-center font-serifAccent text-5xl font-bold tracking-tight text-foreground/[0.04] md:text-[10rem] lg:text-[12rem] select-none whitespace-nowrap">
          PDFWorkSpace
        </h2>
      </div>
    </footer>
  );
};
